/**
 * Runtime resolution of the BFF logging policy: an env-var override layered on
 * top of the per-environment defaults in `logging-defaults.ts` (the "config
 * file"). Each knob is `explicit env var ?? table default for this env`.
 *
 * To change what an environment logs by default, edit `logging-defaults.ts`.
 * The env vars here are per-key runtime overrides:
 *
 *   WRANGO_BFF_LOG_ENABLED           master switch (request / trpc_procedure / page_load)
 *   WRANGO_BFF_LOG_UPSTREAM_ENABLED  upstream_request logger (defaults to the master)
 *   WRANGO_BFF_LOG_REDACT            redactor over server lines
 *
 * Header/body field toggles (a field toggled off is omitted from the line
 * entirely, matching the Python RequestLoggingMiddleware). Same four per surface:
 *
 *   inbound   WRANGO_BFF_LOG_REQUEST_HEADERS / _REQUEST_BODY / _RESPONSE_HEADERS / _RESPONSE_BODY
 *   upstream  WRANGO_BFF_LOG_UPSTREAM_REQUEST_HEADERS / _UPSTREAM_REQUEST_BODY /
 *             _UPSTREAM_RESPONSE_HEADERS / _UPSTREAM_RESPONSE_BODY
 *
 * Any future header/body logger follows the same `FieldToggles` shape — add a
 * table entry + a `*LogFields()` resolver below.
 *
 * Note: enabling a body field in prod logs only blacklist-redacted values — the
 * redactor scrubs *known* sensitive keys, not arbitrary PHI. Enable prod bodies
 * only when the payload is known PHI-free.
 */

import { type FieldToggles, loggingDefaults } from "./logging-defaults";

export type { FieldToggles };

/** Parse a boolean env var; blank/unset/unrecognized → `fallback`. */
export function envBool(value: string | undefined, fallback: boolean): boolean {
  const raw = value?.trim().toLowerCase();
  if (raw === undefined || raw === "") return fallback;
  if (raw === "1" || raw === "true" || raw === "on" || raw === "yes") return true;
  if (raw === "0" || raw === "false" || raw === "off" || raw === "no") return false;
  return fallback;
}

/** Inbound request/response field toggles (table default, per-field env override). */
export function inboundLogFields(): FieldToggles {
  const d = loggingDefaults().inbound;
  return {
    requestHeaders: envBool(process.env.WRANGO_BFF_LOG_REQUEST_HEADERS, d.requestHeaders),
    requestBody: envBool(process.env.WRANGO_BFF_LOG_REQUEST_BODY, d.requestBody),
    responseHeaders: envBool(process.env.WRANGO_BFF_LOG_RESPONSE_HEADERS, d.responseHeaders),
    responseBody: envBool(process.env.WRANGO_BFF_LOG_RESPONSE_BODY, d.responseBody),
  };
}

/** Upstream request/response field toggles ("what the BFF sent" vs "what the API returned"). */
export function upstreamLogFields(): FieldToggles {
  const d = loggingDefaults().upstream;
  return {
    requestHeaders: envBool(process.env.WRANGO_BFF_LOG_UPSTREAM_REQUEST_HEADERS, d.requestHeaders),
    requestBody: envBool(process.env.WRANGO_BFF_LOG_UPSTREAM_REQUEST_BODY, d.requestBody),
    responseHeaders: envBool(process.env.WRANGO_BFF_LOG_UPSTREAM_RESPONSE_HEADERS, d.responseHeaders),
    responseBody: envBool(process.env.WRANGO_BFF_LOG_UPSTREAM_RESPONSE_BODY, d.responseBody),
  };
}

/**
 * Edge page-load field toggles. The edge runs *before* the response and can't read
 * a navigation body, so only `requestHeaders` is applicable (who's calling —
 * user-agent, x-forwarded-for, referer; cookie/auth are redacted). The other three
 * are forced off. Override: `WRANGO_BFF_LOG_EDGE_REQUEST_HEADERS`.
 */
export function edgeLogFields(): FieldToggles {
  return {
    requestHeaders: envBool(
      process.env.WRANGO_BFF_LOG_EDGE_REQUEST_HEADERS,
      loggingDefaults().edge.requestHeaders,
    ),
    requestBody: false,
    responseHeaders: false,
    responseBody: false,
  };
}

/** Whether the redactor runs over server log lines. */
export function serverRedactEnabled(): boolean {
  return envBool(process.env.WRANGO_BFF_LOG_REDACT, loggingDefaults().serverRedact);
}

/**
 * Master switch for the structured server logs (request / trpc_procedure /
 * upstream_request / page_load).
 */
export function serverLoggingEnabled(): boolean {
  return envBool(process.env.WRANGO_BFF_LOG_ENABLED, loggingDefaults().serverEnabled);
}

/**
 * Switch for the **upstream** logger (`upstream_request` → wrango-api / S3;
 * method/url/status/duration, no PHI). Defaults to whatever inbound logging is
 * doing, so opting into local logging with one env var lights up both — but it
 * can be set independently (e.g. keep upstream latency lines on when inbound is
 * off, or on in prod when the master is off).
 */
export function upstreamLoggingEnabled(): boolean {
  return envBool(process.env.WRANGO_BFF_LOG_UPSTREAM_ENABLED, serverLoggingEnabled());
}
